import React, { useState, useMemo } from 'react';
import { Icon } from './icons';
import PerfBlockHeader from './PerfBlockHeader';

const euros = (n) => {
  if (n === null || n === undefined || isNaN(n)) return '—';
  if (n >= 1000) return '€' + (n / 1000).toFixed(n % 1000 ? 1 : 0).replace('.0', '') + 'k';
  return '€' + Math.round(n);
};
function shortCreator(ad) {
  const raw = ad.nomenclature?.creator || (ad.ad_name_sample || '').split('_')[0] || '—';
  return raw.replace(/_/g, ' ');
}

const DRILL = [
  'All test-phase creatives over the last 6 months.',
  'The creatives that hit winner criteria — detected winners (promoted + still pending).',
  'The creatives you actually pushed into the Main adset.',
  'The real scaling winners that cleared the threshold — double down on these.',
];

export default function FunnelBlock({ tested = 0, winnersCount = 0, promotedAds = [], lostPotentialEUR = 0, threshold, onThreshold }) {
  const minSpend = (threshold && threshold.minSpend) ?? 2000;
  const maxCpa = (threshold && threshold.maxCpa) ?? 50;
  const [editing, setEditing] = useState(false);

  const setMin = (v) => onThreshold && onThreshold({ minSpend: v, maxCpa });
  const setMax = (v) => onThreshold && onThreshold({ minSpend, maxCpa: v });

  const isSpending = (ad) => (ad.spend || 0) >= minSpend && ad.cpa !== null && ad.cpa !== undefined && ad.cpa <= maxCpa;
  const promotedN = promotedAds.length;
  const spenders = useMemo(() => promotedAds.filter(isSpending), [promotedAds, minSpend, maxCpa]);
  const spendingN = spenders.length;
  const bestScaler = useMemo(() => {
    const pool = spenders.length ? spenders : promotedAds;
    return pool.slice().sort((a, b) => (b.spend || 0) - (a.spend || 0))[0] || null;
  }, [spenders, promotedAds]);

  const stages = [
    { key: 'tested', label: 'Tested', value: tested, sub: 'unique creatives · 6 mo' },
    { key: 'winners', label: 'Winners detected', value: winnersCount, sub: '≥5 purch in one run' },
    { key: 'promoted', label: 'Promoted to Main', value: promotedN, sub: 'pushed to Main adset' },
    { key: 'spending', label: 'Actually spending', value: spendingN, sub: '≥' + euros(minSpend) + ' · CPA ≤ €' + maxCpa },
  ];
  const base = tested || 1;
  const realRate = tested ? (spendingN / tested * 100) : 0;
  const survival = promotedN ? (spendingN / promotedN * 100) : 0;

  return (
    <section className="perf-block tb-card perf-block-funnel">
      <PerfBlockHeader
        icon={<Icon name="funnel" />}
        title="Pipeline"
        actions={<button type="button" className="perf-hctrl" onClick={() => setEditing(true)}>✎ Edit threshold</button>}
      />
      <div className="perf-block-body">
        {/* Row 1 — the funnel (hover a card for its meaning) */}
        <div className="fnl-stages">
          {stages.map((s, i) => {
            const pct = Math.max(6, s.value / base * 100);
            const conv = i > 0 ? Math.round(s.value / (stages[i - 1].value || 1) * 100) + '%' : null;
            return (
              <div className="fnl-stage" key={s.key}>
                <div className="fnl-card">
                  {conv && <span className="fnl-conv">{conv}</span>}
                  <div className="fnl-l">{s.label}</div>
                  <div className="fnl-v">{s.value}</div>
                  <div className="fnl-s">{s.sub}</div>
                  <div className="fnl-bar"><i style={{ width: pct + '%' }} /></div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Row 2 — the bilan (same card design, 4 cards) */}
        <div className="fnl-stages fnl-bilan">
          <div className="fnl-stage"><div className="fnl-card fnl-card-static"><div className="fnl-l">Real winner rate</div><div className="fnl-v">{realRate.toFixed(1)}%</div><div className="fnl-s">{spendingN} scaling of {tested} tested</div></div></div>
          <div className="fnl-stage"><div className="fnl-card fnl-card-static"><div className="fnl-l">In-Main survival</div><div className="fnl-v">{survival.toFixed(0)}%</div><div className="fnl-s">{spendingN} of {promotedN} promoted still spending</div></div></div>
          <div className="fnl-stage"><div className="fnl-card fnl-card-static"><div className="fnl-l">Best scaler</div><div className="fnl-v">{bestScaler ? euros(bestScaler.spend) : '—'}</div><div className="fnl-s">{bestScaler ? (shortCreator(bestScaler) + ' · €' + (bestScaler.cpa != null ? Math.round(bestScaler.cpa) : '—') + ' CPA') : 'no scaler yet'}</div></div></div>
          <div className="fnl-stage"><div className="fnl-card fnl-card-static"><div className="fnl-l">Lost potential</div><div className="fnl-v">~{euros(lostPotentialEUR)}</div><div className="fnl-s">est · 18% survive × 10× × €8 LTV</div></div></div>
        </div>
      </div>

      {editing && (
        <div onClick={() => setEditing(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000, padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, width: 440, maxWidth: '94vw', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <PerfBlockHeader
              variant="modal"
              title={'Edit "actually spending" threshold'}
              onClose={() => setEditing(false)}
            />
            <div style={{ padding: 18 }}>
              <div style={{ marginBottom: 18 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#444', display: 'flex', justifyContent: 'space-between' }}>Min Main spend <b style={{ color: '#FF7900' }}>{euros(minSpend)}</b></label>
                <input type="range" min="0" max="8000" step="250" value={minSpend} onChange={(e) => setMin(+e.target.value)} style={{ width: '100%', accentColor: '#FF7900', marginTop: 6 }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#444', display: 'flex', justifyContent: 'space-between' }}>Max CPA <b style={{ color: '#FF7900' }}>€{maxCpa}</b></label>
                <input type="range" min="10" max="120" step="5" value={maxCpa} onChange={(e) => setMax(+e.target.value)} style={{ width: '100%', accentColor: '#FF7900', marginTop: 6 }} />
              </div>
              <div style={{ marginTop: 16, fontSize: 12, color: '#8a8a8a', lineHeight: 1.5 }}>This line separates “promoted” from a real scaling winner. It also drives the Spending / Scaling / Underperforming badges in Main adset.</div>
              <button onClick={() => setEditing(false)} style={{ marginTop: 16, width: '100%', background: '#FF7900', color: '#fff', border: 'none', borderRadius: 9, padding: '10px', fontWeight: 700, cursor: 'pointer' }}>Done</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
